import {createEventHandler} from '@wix/tpa-settings'
import {EVENTS_APP_ID, isRtlLanguage} from '@wix/wix-events-commons-statics'
import {ControllerParams, ExperimentsBag, IWixAPI} from '@wix/yoshi-flow-editor'
import {PreloadedState, Store, bindActionCreators} from 'redux'
import {setBaseEnvironment, setFormFactor} from '../../commons/actions/environment'
import {setActiveElement} from '../../commons/actions/focus-handler'
import {setToast} from '../../commons/actions/toast-handler'
import {createUouBiMiddlewareWithBiParams} from '../../commons/bi/bi'
import {Tabs} from '../../commons/enums'
import {isResponsiveEditor} from '../../commons/selectors/environment'
import {getMultilingualInitialState} from '../../commons/services/multilingual'
import {getCurrentMemberId, getMembersAPI} from '../../commons/utils/members-api'
import {createReduxStore, subscribeToStateChanges} from '../../commons/utils/store'
import {getLanguage, getLocale, isSSR} from '../../commons/utils/wix-code-api'
import {MembersSettingsEventsActions, MembersSettingsEventsKeys} from './constants'
import {getComponentData} from './Widget/actions/component'
import {
  closeAllEvents,
  getEvents,
  seeOtherEvents,
  setTab,
  shareEvent,
  toggleEventDetails,
} from './Widget/actions/events'
import {internalNavigate, navigateToDetailsPage, navigateToNoUpcomingEventsUrl} from './Widget/actions/navigation'
import {cancelRsvp} from './Widget/actions/rsvp'
import {downloadTicketsAction, getMyTickets} from './Widget/actions/tickets'
import {showDemoEvents, showNoEvents} from './Widget/actions/view'
import * as eventsUouEvents from './Widget/bi/uou-bi-events-map'
import {datesMiddleware} from './Widget/middlewares/date'
import reducers from './Widget/reducers'
import {getEventList} from './Widget/selectors/events'
import {Actions, MemberPageState, StoreExtraArgs} from './Widget/types/state'
import {Api} from './Widget/utils/api'

interface FetchInitialStateParams {
  controllerParams: ControllerParams
  componentEventHandler: ReturnType<typeof createEventHandler>
}

export const fetchInitialState = async ({controllerParams, componentEventHandler}: FetchInitialStateParams) => {
  const controllerConfig = controllerParams.controllerConfig
  const {appParams, setProps, wixCodeApi, config} = controllerConfig
  const language = getLanguage(wixCodeApi)
  const locale = getLocale(wixCodeApi)
  const experiments = controllerParams.flowAPI.experiments.all()

  const [viewedSiteMemberId, pageUrl] = await Promise.all([
    getSiteMemberId({controllerConfig}),
    getPageUrl(controllerConfig),
  ])

  const store = createStore(controllerParams, experiments, viewedSiteMemberId, pageUrl)
  const actions = exportedActions(store)

  await actions.getComponentData({
    responsive: isResponsiveEditor(config),
    language,
    locale,
    compId: controllerConfig.compId,
    viewMode: wixCodeApi.window.viewMode.toLowerCase(),
  })

  await store.dispatch(setBaseEnvironment() as any)
  await actions.getEvents({tab: Tabs.UPCOMING})

  const state = store.getState()

  setProps({
    state,
    isRendered: true,
    actions,
    cssBaseUrl: appParams.baseUrls.staticsBaseUrl,
    isRTL: isRtlLanguage(language),
    fitToContentHeight: true,
  })

  subscribeToStateChanges(controllerConfig, store)

  const ssr = isSSR(wixCodeApi)

  if (!ssr) {
    actions.getEvents({tab: Tabs.PAST})
  }

  componentEventHandler.on(
    MembersSettingsEventsKeys.ForceView,
    (forceMembersPageUIState: MembersSettingsEventsActions) => {
      if (forceMembersPageUIState === MembersSettingsEventsActions.OpenDefault) {
        store.dispatch(showNoEvents(false))
        store.dispatch(showDemoEvents())
        return store.dispatch(closeAllEvents())
      }

      if (forceMembersPageUIState === MembersSettingsEventsActions.OpenNoEvents) {
        return store.dispatch(showNoEvents(true))
      }

      if (forceMembersPageUIState === MembersSettingsEventsActions.OpenDetails) {
        store.dispatch(showNoEvents(false))
        const events = getEventList(store.getState())
        events.slice(0, 2).forEach(event => store.dispatch(toggleEventDetails(event) as any))
        return
      }
    },
  )
}

const createStore = (controllerParams: ControllerParams, experiments: ExperimentsBag, viewedSiteMemberId, pageUrl) => {
  const serverApi = new Api(controllerParams)
  const controller = controllerParams.controllerConfig

  const initialData: PreloadedState<MemberPageState> = {
    experiments,
    multilingual: getMultilingualInitialState(controller.wixCodeApi),
    user: {
      currentUserId: controller.wixCodeApi.user.currentUser.id,
      viewedSiteMemberId,
    },
  } as PreloadedState<MemberPageState>
  const middleware = [
    createUouBiMiddlewareWithBiParams(
      {
        wixCodeApi: controller.wixCodeApi,
        platformAPIs: controller.platformAPIs,
        appParams: controller.appParams,
        compId: controller.compId,
      },
      eventsUouEvents,
    ),
    datesMiddleware,
  ]

  return createReduxStore<MemberPageState, StoreExtraArgs>({
    initialData,
    extraArguments: {
      wixCodeApi: controller.wixCodeApi,
      serverApi,
      compId: controller.compId,
      baseUrl: controller.appParams.baseUrls.baseUrl,
      pageUrl,
      flowAPI: controllerParams.flowAPI,
    },
    reducers,
    middleware,
  })
}

interface GetSiteMemberIdParams {
  controllerConfig: ControllerParams['controllerConfig']
}

const getSiteMemberId = ({controllerConfig}: GetSiteMemberIdParams) => {
  const {wixCodeApi, appParams} = controllerConfig

  if (appParams.baseUrls.siteMemberId) {
    return appParams.baseUrls.siteMemberId
  }

  if (wixCodeApi.window.viewMode === 'Editor') {
    return handleGetCurrentMemberId(wixCodeApi)
  }

  return getViewedUserId(wixCodeApi)
}

const getViewedUserId = async (wixCodeApi: IWixAPI) => {
  try {
    const membersApi = await getMembersAPI(wixCodeApi)
    return membersApi.getViewedUser()
  } catch (e) {
    return handleGetCurrentMemberId(wixCodeApi)
  }
}

const handleGetCurrentMemberId = (wixCodeApi: IWixAPI) => {
  const currentMemberId = getCurrentMemberId(wixCodeApi)

  if (currentMemberId) {
    return currentMemberId
  }

  // throw new Error('There are no members area installed on this site')
}

const getPageUrl = async ({wixCodeApi}: ControllerParams['controllerConfig']): Promise<string> => {
  const pageUrl = await wixCodeApi.site.getSectionUrl({sectionId: 'events', appDefinitionId: EVENTS_APP_ID})
  return pageUrl?.url ?? null
}

const exportedActions = (store: Store): Actions => {
  const dispatchActions = {
    getComponentData,
    getEvents,
    toggleEventDetails,
    cancelRsvp,
    shareEvent,
    navigateToDetailsPage,
    internalNavigate,
    setTab,
    downloadTicketsAction,
    closeAllEvents,
    seeOtherEvents,
    setFormFactor,
    navigateToNoUpcomingEventsUrl,
    getMyTickets,
    setActiveElement,
    setToast,
  }

  const actions: Actions = {
    ...bindActionCreators(dispatchActions, store.dispatch),
  }

  return actions
}
